import { useState, useMemo, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/AuthContext.jsx';
import {
  useCan,
  useCanAsMember,
  useCanReadOrgProfile,
  usePreference,
} from '@/hooks';
import {
  createPreference,
  deriveUserInitials,
  deriveFullUserName,
} from '@/lib';
import {
  CAPABILITY_FULL_LABELS,
  LAYOUT_CAPABLE,
  getOrgColor,
  orgArea,
} from '@/app/constants.js';
import Sidebar, {
  NAV_GROUPS,
  BACKOFFICE_NAV_GROUPS,
  resolveNavGroups,
} from '@/shared/Sidebar/Sidebar';
import Topbar from '@/shared/Topbar/Topbar';
import {
  BuildingIcon,
  UserIcon,
  LogoutIcon,
  SwapIcon,
  ChevronRightIcon,
} from '@/ui/icons';
import CommandPalette from '@/shared/CommandPalette';
import ShortcutsOverlay from '@/shared/ShortcutsOverlay';
import NuevaDiligenciaHost from '@/pages/diligencias/NuevaDiligenciaHost.jsx';
import FeedbackWidget from '@/shared/FeedbackWidget/FeedbackWidget.jsx';
import { ConfirmProvider, ToastProvider } from '@/ui';

import './MainLayout.css';

// Active-state paths and the "g <letter>" chord derive from the Sidebar's
// NAV_GROUPS and BACKOFFICE_NAV_GROUPS, so they follow the menu: retiring an
// item drops its chord.
const FIRM_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);
const SIDEBAR_PATHS = FIRM_NAV_ITEMS.map((i) => i.path);
const NAV_SHORTCUTS = Object.fromEntries(
  FIRM_NAV_ITEMS.filter((i) => i.shortcut).map((i) => [i.shortcut, i.path]),
);
const G_CHORD_MS = 1200;

// The collapsed rail is a stored preference, so it survives a reload. The `[`
// shortcut and the sidebar button both set it.
const sidebarCollapsedPref = createPreference('rec:sidebar-collapsed', {
  fallback: false,
  encode: (b) => (b ? '1' : '0'),
  decode: (s) => s === '1',
});

// Backoffice nav: same pattern, derived from its own group.
const BACKOFFICE_NAV_ITEMS = BACKOFFICE_NAV_GROUPS.flatMap((g) => g.items);
const BACKOFFICE_SIDEBAR_PATHS = BACKOFFICE_NAV_ITEMS.map((i) => i.path);
// Backoffice chords follow the filtered nav: an item hidden by a permission
// takes its chord with it.
function navShortcutsOf(groups) {
  return Object.fromEntries(
    groups
      .flatMap((g) => g.items)
      .filter((i) => i.shortcut)
      .map((i) => [i.shortcut, i.path]),
  );
}

function pathToActive(pathname, paths) {
  if (pathname === '/' || pathname === '/inicio' || pathname === '/dashboard') {
    return '/';
  }
  return (
    paths.find((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? null
  );
}

function deriveUserName(user) {
  if (!user) return '';
  const first = user.firstName ?? '';
  const lastInitial = (user.lastName ?? '').charAt(0);
  const formatted = `${first}${lastInitial ? ` ${lastInitial}.` : ''}`.trim();
  return formatted || user.email || '';
}

// Two-letter org initials for the account-menu header avatar.
function deriveOrgInitials(name) {
  if (!name) return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export default function MainLayout() {
  const {
    activeCapability,
    user,
    organizationName,
    organizationId,
    memberships,
    switchOrganization,
    logout,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [pageBreadcrumb, setPageBreadcrumb] = useState([]);
  const [pills, setPills] = useState([]);
  const [hasNotification, setHasNotification] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] =
    usePreference(sidebarCollapsedPref);
  // Responsive nav (applies to EVERY explorer, since they all live in this
  // layout): below this width the 216px rail + the widest explorer's table +
  // toolbar stop fitting, so the nav auto-collapses to the 52px icon rail,
  // reclaiming 164px for content. The threshold lives in a media query — the
  // idiomatic place for a responsive breakpoint — not as a pixel value
  // hand-fixed in render logic. The labels are JS-rendered on `collapsed`, so a
  // pure-CSS hide isn't possible; matchMedia drives the prop.
  const [autoCollapse, setAutoCollapse] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 900px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 900px)');
    const onChange = (e) => setAutoCollapse(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // Some sections (e.g. /sistema) own their full breadcrumb and omit the
  // active-organization head crumb.
  const [hideOrgCrumb, setHideOrgCrumb] = useState(false);
  // Timestamp of the last bare "g" press, to detect the g-then-letter chord.
  const pendingGRef = useRef(0);

  const hasLayout = LAYOUT_CAPABLE.has(activeCapability);
  // El layout es compartido; la nav cambia por capability. Backoffice ve solo
  // "Plataforma · Pagos" — nunca las rutas del estudio.
  const isBackoffice = activeCapability === 'backoffice';
  const canReadBilling = useCan('billing:read');
  // Documentos (backoffice) mirrors the API gate, which lets an owner through.
  const canManualPayout = useCanAsMember('payout:manual');
  // Configuración (backoffice) mirrors the API gate on platform settings,
  // which lets an owner through.
  const canReadPlatformSettings = useCanAsMember('platform-settings:read');
  // "Ver estudio" opens a page that reads the org profile and members.
  const canReadOrgProfile = useCanReadOrgProfile();
  // Nav per capability, with gated items filtered: Facturación by billing:read
  // in the firm nav (empty groups drop out), Documentos by payout:manual in the
  // backoffice nav.
  const navGroups = useMemo(
    () =>
      resolveNavGroups(activeCapability, {
        canReadBilling,
        canManualPayout,
        canReadPlatformSettings,
      }),
    [
      activeCapability,
      canReadBilling,
      canManualPayout,
      canReadPlatformSettings,
    ],
  );
  // Kept apart from navGroups, which also feed the sidebar and
  // ShortcutsOverlay, so a palette action shows up in neither.
  // "Nueva diligencia" carries `background`, so NuevaDiligenciaHost opens it
  // as a modal over the current route.
  const paletteActions = useMemo(
    () => [
      { label: 'Suscribir causa', path: '/causas/nueva' },
      {
        label: 'Nueva diligencia',
        path: '/diligencias/nueva',
        state: { background: location },
      },
      { label: 'Ir a pagos', path: '/pagos' },
    ],
    [location],
  );
  const navShortcuts = useMemo(
    () => (isBackoffice ? navShortcutsOf(navGroups) : NAV_SHORTCUTS),
    [isBackoffice, navGroups],
  );
  const sidebarPaths = isBackoffice ? BACKOFFICE_SIDEBAR_PATHS : SIDEBAR_PATHS;
  const activeItem = pathToActive(location.pathname, sidebarPaths);
  const userName = deriveUserName(user);
  const userInitials = deriveUserInitials(user);
  // Effective collapse = manual toggle OR the narrow-viewport auto-collapse.
  const collapsed = sidebarCollapsed || autoCollapse;

  const breadcrumb = useMemo(() => {
    const head =
      !hideOrgCrumb && organizationName
        ? [{ label: organizationName, onClick: () => navigate('/') }]
        : [];
    return [...head, ...pageBreadcrumb];
  }, [organizationName, pageBreadcrumb, navigate, hideOrgCrumb]);

  // Account menu items. Hierarchy: the person's account first (identity + "Mi
  // cuenta"), then the organization (org identity + "Ver estudio" + switcher),
  // then "Cerrar sesión". DropdownMenu reads `item.text` for interactive rows.
  const userMenuItems = useMemo(() => {
    const items = [];
    const isFirm =
      activeCapability === 'enterprise_law_firm' ||
      activeCapability === 'solo_law_firm';
    const orgList = Array.isArray(memberships) ? memberships : [];

    // Account (user) header — top of the hierarchy.
    items.push({
      type: 'header',
      avatar: deriveUserInitials(user),
      title: deriveFullUserName(user),
      subtitle: user?.email ?? '',
    });
    items.push({
      type: 'item',
      ico: <UserIcon size={15} />,
      text: 'Mi cuenta',
      onSelect: () => navigate('/sistema/cuenta'),
    });

    // Organization block — only firm orgs have an org-settings area ("Ver
    // estudio"); the switcher appears when the user belongs to more than one org.
    if (isFirm || orgList.length > 1) {
      items.push({ type: 'sep' });
      items.push({
        type: 'userblock',
        name: organizationName,
        meta: CAPABILITY_FULL_LABELS[activeCapability] ?? '',
      });
      if (isFirm && canReadOrgProfile) {
        items.push({
          type: 'item',
          ico: <BuildingIcon size={15} />,
          text: `Ver ${orgArea(activeCapability).nav.toLowerCase()}`,
          onSelect: () => navigate('/sistema/organizacion'),
        });
      }
      if (orgList.length > 1) {
        items.push({
          type: 'item',
          ico: <SwapIcon size={15} />,
          text: 'Cambiar de organización',
          chev: <ChevronRightIcon size={14} />,
          orgSwitch: true,
          // onSelect omitted; SidebarAccount detects `orgSwitch` and wires
          // () => setOrgView(true) + keepOpen:true.
        });
      }
    }

    if (logout) {
      items.push({ type: 'sep' });
      items.push({
        type: 'item',
        ico: <LogoutIcon size={15} />,
        text: 'Cerrar sesión',
        onSelect: () => logout(),
      });
    }
    return items;
  }, [
    activeCapability,
    organizationName,
    memberships,
    logout,
    navigate,
    user,
    canReadOrgProfile,
  ]);

  // Org list for the Sidebar's drill-in org-switcher sub-view.
  const accountOrgs = useMemo(() => {
    const orgList = Array.isArray(memberships) ? memberships : [];
    if (orgList.length <= 1) return [];
    return orgList.map((m) => ({
      id: m.organizationId,
      name: m.organizationName,
      // Memberships carry `capabilities: [...]`; the first one names the org kind.
      meta: CAPABILITY_FULL_LABELS[m.capabilities?.[0]] ?? '',
      initials: deriveOrgInitials(m.organizationName),
      color: getOrgColor(m.organizationId),
      active: m.organizationId === organizationId,
    }));
  }, [memberships, organizationId]);

  const outletContext = useMemo(
    () => ({
      setBreadcrumb: setPageBreadcrumb,
      setPills,
      setHasNotification,
      setHideOrgCrumb,
    }),
    [],
  );

  // Wire pill kbd shortcuts to global keydown — single-letter keys fire the
  // matching pill's onClick when no input/textarea/contentEditable is focused
  // and no modifier is held. Mirrors the hint shown next to each pill.
  useEffect(() => {
    function handleKey(e) {
      // Command palette (⌘K / Ctrl+K) — works even while a field is focused.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setShortcutsOpen(false);
        setPaletteOpen(true);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      if (target) {
        const tag = target.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }

      // Keyboard-shortcuts cheatsheet ("?").
      if (e.key === '?') {
        e.preventDefault();
        setPaletteOpen(false);
        setShortcutsOpen(true);
        return;
      }

      // Toggle the sidebar with "[".
      if (e.key === '[') {
        e.preventDefault();
        pendingGRef.current = 0;
        setSidebarCollapsed((v) => !v);
        return;
      }

      const key = e.key?.length === 1 ? e.key.toUpperCase() : null;
      if (!key) return;

      // Second key of the "g then <letter>" navigation chord.
      if (Date.now() - pendingGRef.current < G_CHORD_MS) {
        pendingGRef.current = 0;
        const path = navShortcuts[key];
        if (path) {
          e.preventDefault();
          navigate(path);
          return;
        }
      }

      // First key of the chord — arm it and swallow the keystroke.
      if (key === 'G') {
        pendingGRef.current = Date.now();
        return;
      }

      // Otherwise, fall back to single-key pill shortcuts.
      const match = pills.find(
        (p) => !p.disabled && p.kbd && p.kbd.toUpperCase() === key && p.onClick,
      );
      if (match) {
        e.preventDefault();
        match.onClick();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [pills, navigate, navShortcuts]);

  return (
    <ToastProvider>
      <ConfirmProvider>
        <div className={`lb-layout${collapsed ? ' lb-layout--collapsed' : ''}`}>
          <Sidebar
            activeItem={activeItem}
            onNavigate={navigate}
            collapsed={collapsed}
            onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
            showCollapseToggle={!autoCollapse}
            orgLabel={orgArea(activeCapability).nav}
            groups={navGroups}
            account={{
              initials: userInitials,
              name: userName,
              orgName: organizationName,
              menuItems: userMenuItems,
              orgs: accountOrgs,
              onSwitchOrg: switchOrganization ?? undefined,
            }}
          />
          <div className="lb-layout__main">
            <Topbar
              breadcrumb={breadcrumb}
              pills={pills}
              hasNotification={hasNotification}
              onOpenSearch={() => setPaletteOpen(true)}
            />
            {hasLayout ? (
              <Outlet context={outletContext} />
            ) : (
              <div className="lb-layout__disabled">
                <h2 className="lb-layout__disabled-title">
                  Vista no disponible
                </h2>
                <p className="lb-layout__disabled-subtitle">
                  Esta vista no está habilitada para este tipo de perfil.
                </p>
              </div>
            )}
          </div>
          <FeedbackWidget />
          {/* Route-anchored modals mount over whatever view is current (the
              routes render the background location; the host reads the real
              one). Inside the providers: the create flow needs toast/confirm. */}
          <NuevaDiligenciaHost />
          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            groups={navGroups}
            actions={paletteActions}
            onNavigate={navigate}
          />
          <ShortcutsOverlay
            open={shortcutsOpen}
            onClose={() => setShortcutsOpen(false)}
            groups={navGroups}
          />
        </div>
      </ConfirmProvider>
    </ToastProvider>
  );
}
